import { Controller } from "@hotwired/stimulus"

// The per-page help popover (the "?" control in the operator header). Two jobs:
//   1. Open/close the panel (click to open, click-outside or Escape to close),
//      mirroring dropdown_controller.
//   2. Navigate the panel between the TOPIC LIST and a selected topic's VIDEO,
//      and back. The list and every topic panel are server-rendered (so the
//      titles/tips are present for tests and crawlers); this controller just
//      shows/hides them and pauses video when you leave a topic.
export default class extends Controller {
  static targets = ["panel", "list", "topic"]

  connect() {
    this.boundClose = this.closeOnOutside.bind(this)
    this.boundKeydown = this.closeOnEscape.bind(this)
    this.isOpen = false
  }

  disconnect() {
    this.removeListeners()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    this.showList() // always open on the topic list
    this.isOpen = true
    setTimeout(() => {
      document.addEventListener("click", this.boundClose)
      document.addEventListener("keydown", this.boundKeydown)
    }, 0)
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.pauseAll()
    this.isOpen = false
    this.removeListeners()
  }

  // Show one topic's video panel, hide the list and the other topics. Widen the
  // popover on desktop so the video is watchable (the narrow tips width squeezes
  // a screen recording down to ~320px).
  showTopic(event) {
    event.preventDefault()
    const index = event.currentTarget.dataset.index
    if (this.hasListTarget) this.listTarget.classList.add("hidden")
    this.topicTargets.forEach((el) => {
      const match = el.dataset.index === index
      el.classList.toggle("hidden", !match)
      if (!match) this.pauseVideoIn(el)
    })
    this.setWide(true)
  }

  // Back to the topic list; pause any playing video and return to the narrow
  // tips width.
  showList(event) {
    if (event) event.preventDefault()
    this.pauseAll()
    this.topicTargets.forEach((el) => el.classList.add("hidden"))
    if (this.hasListTarget) this.listTarget.classList.remove("hidden")
    this.setWide(false)
  }

  // Swap the panel between the narrow tips width (sm:w-80) and the wide video
  // width (desktop only; see .page-help-panel--wide in the stylesheet).
  setWide(wide) {
    this.panelTarget.classList.toggle("page-help-panel--wide", wide)
    this.panelTarget.classList.toggle("sm:w-80", !wide)
  }

  pauseAll() {
    this.topicTargets.forEach((el) => this.pauseVideoIn(el))
  }

  pauseVideoIn(el) {
    const v = el.querySelector("video")
    if (v && !v.paused) v.pause()
  }

  closeOnOutside(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }

  closeOnEscape(event) {
    if (event.key !== "Escape") return
    // Escape backs out of a topic to the list first, then closes.
    const onTopic = this.hasListTarget && this.listTarget.classList.contains("hidden")
    onTopic ? this.showList() : this.close()
  }

  removeListeners() {
    document.removeEventListener("click", this.boundClose)
    document.removeEventListener("keydown", this.boundKeydown)
  }
}
